import { APP_BASE_HREF, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationError, NavigationStart, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { timer } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import { checkForUpdateInterval, firstCheckForUpdateDelay, snackBarInterval } from '../constants/app.constants';

@Injectable({
  providedIn: 'root',
})
export class ApplicationUpdateService {
  private readonly _baseHref = inject(APP_BASE_HREF);
  private readonly _document = inject(DOCUMENT);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _updates = inject(SwUpdate, { optional: true });
  private readonly _translateService = inject(TranslateService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _router = inject(Router);
  private _isReloadRequired = false;

  constructor() {
    if (isPlatformBrowser(this._platformId) && this._updates?.isEnabled) {
      this._watchForUpdates();
      this._watchForRecoverability();
      this._watchForRouting();
    }
  }

  public get isReloadRequired(): boolean {
    return this._isReloadRequired;
  }

  private _watchForUpdates(): void {
    this._updates?.versionUpdates
      .pipe(
        filter(e => e.type === 'VERSION_READY'),
        switchMap(() => {
            this._isReloadRequired = true;

            return this._snackBar
              .open(
                this._translateService.instant('common.text.new-version'),
                this._translateService.instant('common.label.force-update'),
                {
                  duration: snackBarInterval,
                  verticalPosition: 'bottom',
                },
              )
              .onAction();
          },
        ),
      )
      .subscribe(() => this._reload());

    timer(firstCheckForUpdateDelay, checkForUpdateInterval)
      .subscribe(() => this._updates?.checkForUpdate());
  }

  private _watchForRecoverability(): void {
    this._updates?.unrecoverable
      .pipe(
        switchMap(() => this._snackBar.open(
          this._translateService.instant('common.text.unrecoverable-version'),
          this._translateService.instant('common.label.force-update'),
          {
            duration: snackBarInterval,
            verticalPosition: 'bottom',
          },
        ).afterDismissed()),
      )
      .subscribe(() => this._reload());
  }

  private _reload(): void {
    this._document.location.reload();
  }

  private _navigate(url: string): void {
    this._document.location.href = `${this._document.location.origin}${this._baseHref}${url}`;
  }

  private _watchForRouting() {
    this._router.events
      .pipe(filter(e => e instanceof NavigationError || e instanceof NavigationStart))
      .subscribe(e => {
        if (this._isReloadRequired) {
          this._navigate(e.url);
        } else if (e instanceof NavigationError) {
          this._tryToUpdate(e);
        }
      });
  }

  private async _tryToUpdate(e: NavigationError): Promise<void> {
    const available = await this._updates?.checkForUpdate();

    if (available) {
      this._navigate(e.url);
    }
  }
}
